import React, {useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  HardHat, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  BarChart3,
  Clock,
  Shield,
  Target,
  Eye,
  Play,
  Construction,
  AlertTriangle,
  Monitor,
  Settings
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const ConstructionPage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  
 

  const challenges = [
    'On-site safety violations and non-compliance with PPE protocols',
    'Lack of coordination among field teams and project managers',
    'Difficulty tracking progress across multiple sites',
    'Delays due to unplanned equipment downtime',
    'Manual and outdated reporting systems',
    'Inconsistent communication between on-site and back-office teams, leading to delays in decision-making and approvals'
  ];

  const solutions = [
    {
      name: 'Planfusion',
      path: '/products/ai/planfusion',
      description: 'All-in-one planning hub that aligns goals, projects, and resources.',
      benefits: ['Unified roadmap', 'Team collaboration', 'Scenario forecasting']
    },
    {
      name: 'WhispeRAGon',
      path: '/products/ai/whisperagon',
      description: 'Voice-native RAG that turns conversations into accurate, grounded answers.',
      benefits: ['Cited answers', 'Context retrieval', 'Voice transcription']
    },
    {
      name: 'ManageRTC',
      path: '/products/it/ManageRTC',
      description: 'Centralize and control your real-time voice, video, and messaging.',
      benefits: ['Cost analytics', 'Policy routing', 'QoS monitoring']
    }
  ];

  const keyFeatures = [
    {
      icon: Monitor,
      title: 'Real-time Monitoring',
      description: 'Continuous oversight of construction sites, equipment, and safety compliance with instant alerts and notifications.'
    },
    {
      icon: Settings,
      title: 'Seamless Field-to-Office Integration',
      description: 'Connect on-site operations with back-office management for improved coordination and decision-making.'
    },
    {
      icon: Shield,
      title: 'Enhanced Worker Safety',
      description: 'Advanced safety monitoring systems that protect workers and ensure compliance with industry regulations.'
    },
    {
      icon: Target,
      title: 'Scalable & Flexible',
      description: 'Solutions that adapt to projects of any size, from small renovations to large-scale infrastructure developments.'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Project Efficiency',
      description: 'Streamlined operations and real-time monitoring reduce project timelines and improve delivery.',
      metric: '35% faster completion'
    },
    {
      icon: Shield,
      title: 'Safety Improvement',
      description: 'Advanced safety monitoring and PPE compliance significantly reduce workplace accidents.',
      metric: '85% accident reduction'
    },
    {
      icon: Clock,
      title: 'Equipment Uptime',
      description: 'Predictive maintenance prevents equipment failures and minimizes costly downtime.',
      metric: '90% uptime improvement'
    },
    {
      icon: BarChart3,
      title: 'Cost Savings',
      description: 'Optimized resource allocation and preventive maintenance deliver significant cost reductions.',
      metric: '45% cost reduction'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-orange-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.5,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-orange-500 to-red-600 neon-glow">
                  <HardHat className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-orange-400 uppercase tracking-wide">Smart Construction Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Construction <span className="bg-linear-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">Industry</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-orange-300"
              >
                Building the Future with Smart Technologies
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The construction industry is undergoing a digital revolution. As projects become more complex and safety regulations 
                grow stricter, forward-thinking companies are embracing AI, IoT, and IT solutions to build smarter, safer, and faster. 
                At amasQIS.ai, we help construction firms leverage digital innovation to streamline operations and deliver projects on time.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-orange-500 to-red-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
                >
                  Build Smarter
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-orange-500 to-red-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/construction-in.png'
                    alt="Construction Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-orange-500/20 rounded-lg backdrop-blur-sm">
                          <HardHat className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Construction Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-orange-500">
                    <Construction className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">BUILDING</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Sector Challenges in Construction
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Construction companies face unique operational challenges that require innovative digital solutions for safety, efficiency, and project success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for the Construction Industry
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Comprehensive digital solutions designed to address the unique challenges of modern construction operations.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-orange-400 group-hover:text-orange-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-orange-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-red-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Construction Companies Choose amasQIS.ai
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              We're more than just a tech provider - we're a strategic partner for digital construction success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Construction Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Transform your construction operations with quantifiable improvements in safety, efficiency, and project delivery.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-orange-500/20">
                      <Icon className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-red-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Build the Future?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Join leading construction companies that trust amasQIS.ai to deliver safer, smarter, and more efficient construction solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-orange-500 to-red-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
              >
                Build Smarter
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { ConstructionPage };
